import { useState, useMemo } from "react";

const PRESETS = [
  { label: "24h", days: 1 },
  { label: "7d", days: 7 },
  { label: "30d", days: 30 },
] as const;

function toISODate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function daysAgo(days: number): Date {
  const d = new Date();
  d.setDate(d.getDate() - days);
  d.setHours(0, 0, 0, 0);
  return d;
}

function endOfToday(): Date {
  const d = new Date();
  d.setHours(23, 59, 59, 999);
  return d;
}

export interface TimeRange {
  from: string; // ISO 8601
  to: string; // ISO 8601
}

export function useTimeRange() {
  const [fromDate, setFromDate] = useState(() => toISODate(daysAgo(7)));
  const [toDate, setToDate] = useState(() => toISODate(endOfToday()));

  const range: TimeRange = useMemo(
    () => ({
      from: new Date(fromDate + "T00:00:00Z").toISOString(),
      to: new Date(toDate + "T23:59:59Z").toISOString(),
    }),
    [fromDate, toDate],
  );

  return { fromDate, toDate, setFromDate, setToDate, range };
}

interface TimeRangeSelectorProps {
  fromDate: string;
  toDate: string;
  onFromChange: (value: string) => void;
  onToChange: (value: string) => void;
}

function isActivePreset(fromDate: string, toDate: string, days: number): boolean {
  return fromDate === toISODate(daysAgo(days)) && toDate === toISODate(endOfToday());
}

export function TimeRangeSelector({
  fromDate,
  toDate,
  onFromChange,
  onToChange,
}: TimeRangeSelectorProps) {
  return (
    <div className="flex items-center gap-2">
      <div className="inline-flex items-center rounded-md border border-line-subtle bg-canvas p-0.5">
        {PRESETS.map((preset) => {
          const active = isActivePreset(fromDate, toDate, preset.days);
          return (
            <button
              key={preset.label}
              type="button"
              aria-pressed={active}
              onClick={() => {
                onFromChange(toISODate(daysAgo(preset.days)));
                onToChange(toISODate(endOfToday()));
              }}
              className={`rounded px-2.5 py-1 text-xs font-medium transition-colors ${
                active
                  ? "bg-electric-blue/15 text-electric-blue"
                  : "text-fg-muted hover:text-fg"
              }`}
            >
              {preset.label}
            </button>
          );
        })}
      </div>
      <div className="flex items-center gap-1.5 text-xs">
        <input
          type="date"
          value={fromDate}
          onChange={(e) => onFromChange(e.target.value)}
          className="rounded-md border border-line bg-canvas px-2 py-1 text-xs text-fg focus:border-electric-blue focus:outline-none focus:ring-1 focus:ring-electric-blue/50"
        />
        <span className="text-fg-faint">→</span>
        <input
          type="date"
          value={toDate}
          onChange={(e) => onToChange(e.target.value)}
          className="rounded-md border border-line bg-canvas px-2 py-1 text-xs text-fg focus:border-electric-blue focus:outline-none focus:ring-1 focus:ring-electric-blue/50"
        />
      </div>
    </div>
  );
}
